import React, { useState, useEffect, useCallback } from 'react';
import { getPublicGallery } from '../api/gallery';
import { getImageUrlById } from '../api/images';
import type { Gallery } from '../features/gallery/types';
import { withTimeout } from '../shared/lib/withTimeout';

const PUBLIC_GALLERY_TIMEOUT_MS = 2500;

const GallerySection: React.FC = () => {
  const [galleryItems, setGalleryItems] = useState<Gallery[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  useEffect(() => {
    const loadGallery = async () => {
      try {
        setIsLoading(true);
        const items = await withTimeout(
          getPublicGallery(),
          PUBLIC_GALLERY_TIMEOUT_MS,
          'Public gallery load timeout'
        );
        setGalleryItems(items);
      } catch (err) {
        console.warn('Public gallery skipped on home page', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadGallery();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (galleryItems.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [galleryItems.length]);

  // Keyboard navigation for fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (e.key === 'ArrowLeft') {
        setFullscreenIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setFullscreenIndex((prev) => (prev + 1) % galleryItems.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, galleryItems.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % galleryItems.length);
  }, [galleryItems.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  }, [galleryItems.length]);

  const openFullscreen = useCallback((index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const goFullscreenNext = useCallback(() => {
    setFullscreenIndex((prev) => (prev + 1) % galleryItems.length);
  }, [galleryItems.length]);

  const goFullscreenPrev = useCallback(() => {
    setFullscreenIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  }, [galleryItems.length]);

  if (isLoading) {
    return (
      <section id="gallery" className="gallery-section py-5">
        <div className="container">
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading gallery...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (galleryItems.length === 0) {
    return null; // Don't show section if no gallery items
  }

  const currentItem = galleryItems[currentIndex];
  const fullscreenItem = galleryItems[fullscreenIndex];

  return (
    <section id="gallery" className="gallery-section py-5">
      <div className="container">
        <div className="row mb-4">
          <div className="col-12 text-center">
            <h2 className="section-title">Gallery</h2>
            <p className="section-subtitle text-muted">
              Moments from our community
            </p>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="gallery-carousel">
              <div className="gallery-carousel__main">
                {/* Navigation Arrows */}
                {galleryItems.length > 1 && (
                  <>
                    <button
                      className="gallery-carousel__nav gallery-carousel__nav--prev"
                      onClick={goPrev}
                      aria-label="Previous slide"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button
                      className="gallery-carousel__nav gallery-carousel__nav--next"
                      onClick={goNext}
                      aria-label="Next slide"
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </>
                )}

                {/* Image Container */}
                <div
                  className="gallery-carousel__image-container"
                  onClick={() => openFullscreen(currentIndex)}
                  style={{ cursor: 'pointer' }}
                >
                  {currentItem?.imageId ? (
                    <img
                      src={getImageUrlById(currentItem.imageId)}
                      alt={currentItem.title}
                      className="gallery-carousel__image"
                    />
                  ) : (
                    <div className="gallery-carousel__placeholder">
                      <i className="bi bi-image"></i>
                    </div>
                  )}

                  {/* Overlay with title and fullscreen hint */}
                  <div className="gallery-carousel__overlay">
                    <h3 className="gallery-carousel__title">{currentItem?.title}</h3>
                    <span className="gallery-carousel__fullscreen-hint">
                      <i className="bi bi-fullscreen me-1"></i>
                      Click to expand
                    </span>
                  </div>
                </div>
              </div>

              {/* Description below image */}
              {currentItem?.descriptionHtml && (
                <div
                  className="gallery-carousel__description rich-text-content"
                  dangerouslySetInnerHTML={{ __html: currentItem.descriptionHtml }}
                />
              )}

              {/* Dots Indicator */}
              {galleryItems.length > 1 && (
                <div className="gallery-carousel__dots">
                  {galleryItems.map((_, index) => (
                    <button
                      key={index}
                      className={`gallery-carousel__dot ${index === currentIndex ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      {isFullscreen && fullscreenItem && (
        <div className="gallery-lightbox" onClick={closeFullscreen}>
          {/* Close button */}
          <button
            className="gallery-lightbox__close"
            onClick={closeFullscreen}
            aria-label="Close fullscreen"
          >
            <i className="bi bi-x-lg"></i>
          </button>

          {/* Navigation */}
          {galleryItems.length > 1 && (
            <>
              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                onClick={(e) => { e.stopPropagation(); goFullscreenPrev(); }}
                aria-label="Previous image"
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                onClick={(e) => { e.stopPropagation(); goFullscreenNext(); }}
                aria-label="Next image"
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}

          {/* Image */}
          <div className="gallery-lightbox__content" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-lightbox__layout">
              <div className="gallery-lightbox__image-section">
                {fullscreenItem.imageId ? (
                  <img
                    src={getImageUrlById(fullscreenItem.imageId)}
                    alt={fullscreenItem.title}
                    className="gallery-lightbox__image"
                  />
                ) : (
                  <div className="gallery-lightbox__placeholder">
                    <i className="bi bi-image"></i>
                  </div>
                )}
              </div>

              {/* Info card (matches Important Notices layout) */}
              <div className="gallery-lightbox__info-card">
                <h3 className="gallery-lightbox__info-title">{fullscreenItem.title}</h3>
                {fullscreenItem.descriptionHtml && (
                  <div
                    className="gallery-lightbox__info-description rich-text-content"
                    dangerouslySetInnerHTML={{ __html: fullscreenItem.descriptionHtml }}
                  />
                )}
                <div className="gallery-lightbox__info-counter">
                  {fullscreenIndex + 1} / {galleryItems.length}
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          {galleryItems.length > 1 && (
            <div className="gallery-lightbox__dots">
              {galleryItems.map((_, index) => (
                <button
                  key={index}
                  className={`gallery-lightbox__dot ${index === fullscreenIndex ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setFullscreenIndex(index); }}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default GallerySection;
